import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { setModal } from '../../slices/modalSlice'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import * as categoriasService from '../servicios/categoriasService'
import * as publicacionService from '../servicios/publicacionesService'
import styles from './Descripcion.module.css'
import { MetadatosCampos } from './MetadatosCampos'

const MAX_METADATOS_SIN_MODAL = 6

const Metadatos = ({
  idPublicacion = 0,
  editable = false,
  idCategoria = 0,
  esPublicar = false,
  value,
  onChange = () => {},
  onChangeExterno = () => {}
}) => {
  const [metaDatos, setMetaDatos] = useState([])
  const [metaDatosOrdenados, setMetaDatosOrdenados] = useState([])
  const [componenteModal, setComponenteModal] = useState(null)
  const dispatch = useDispatch()
  const metaDatosInicialesRef = useRef({})
  const valoresActualesRef = useRef({})

  const obtenerMetaDatosPublicacion = useCallback(async () => {
    if (idPublicacion > 0) {
      const { data } = await publicacionService.buscarMetadato(idPublicacion)
      setMetaDatos(data)
      const mapa = {}
      data.forEach((m) => {
        mapa[m.idMetaDato] = m.valor || null
      })
      metaDatosInicialesRef.current = mapa
      valoresActualesRef.current = { ...mapa }
    }
  }, [idPublicacion])

  const obtenerMetadatosPublicar = async () => {
    if (idCategoria > 0) {
      const { data } = await categoriasService.buscarMetadato(idCategoria)
      setMetaDatos(data.length > 0 ? data : [])
    }
  }

  useEffect(() => {
    if (!esPublicar) {
      obtenerMetaDatosPublicacion()
    }
  }, [obtenerMetaDatosPublicacion, esPublicar])

  useEffect(() => {
    if (idCategoria > 0) {
      obtenerMetadatosPublicar()
    }
  }, [idCategoria])

  useEffect(() => {
    const ordenados = (metaDatos || [])
      .map((metadato, index) => {
        const valorActual = getValorActual(metadato)
        const completado = Boolean(String(valorActual || '').trim())
        return { ...metadato, valorActual, completado, index }
      })
      .sort((a, b) => {
        if (a.completado !== b.completado) {
          return a.completado ? -1 : 1
        }
        return a.index - b.index
      })
    setMetaDatosOrdenados(ordenados)
  }, [idPublicacion, idCategoria, metaDatos])

  const guardarMetaDatoAdministrar = async (idMetaDato, valor) => {
    try {
      if (!idPublicacion) {
        return
      }
      const valorNormalizado = valor?.trim() || null
      const metadato = {
        idPublicacion,
        idMetaDato,
        valor: valorNormalizado
      }

      await publicacionService.InsertarMetadato(metadato)
      valoresActualesRef.current[idMetaDato] = valorNormalizado
      const cambio = Object.keys(metaDatosInicialesRef.current).some(
        (key) => valoresActualesRef.current[key] !== metaDatosInicialesRef.current[key]
      )
      onChangeExterno(cambio)
    } catch (error) {
      const mensaje = 'Error al insertar la característica'
      toastErrorLog(error, mensaje, 'caracteristica')
    }
  }

  const actualizarMetadato = (idMetaDato, valorBase, unidad = '') => {
    const valorFinal = `${valorBase} ${unidad}`.trim()
    if (esPublicar) {
      const metadatosValues = [...(value?.metadatos || [])]
      const index = metadatosValues.findIndex((m) => m.IdMetaDato === idMetaDato)

      if (!valorFinal) {
        if (index !== -1) {
          metadatosValues.splice(index, 1)
        }
      } else if (index !== -1) {
        metadatosValues[index] = { IdMetaDato: idMetaDato, valor: valorFinal }
      } else {
        metadatosValues.push({ IdMetaDato: idMetaDato, valor: valorFinal })
      }

      onChange(
        { currentTarget: { name: 'metadatos', value: metadatosValues } },
        'calc',
        metadatosValues
      )
    } else {
      setMetaDatosOrdenados((prev) =>
        prev.map((m) => (m.idMetaDato === idMetaDato ? { ...m, valorActual: valorFinal } : m))
      )
    }
  }

  const getValorActual = (metadato) => {
    if (esPublicar) {
      return value?.metadatos?.find((m) => m.IdMetaDato === metadato.idMetaDato)?.valor || ''
    }
    return metadato.valor || ''
  }

  const mostrarConModal =
    metaDatosOrdenados.length > MAX_METADATOS_SIN_MODAL && !esPublicar && editable

  const metadatoCamposProps = {
    actualizarMetadato,
    editable,
    esPublicar,
    guardarMetaDatoAdministrar
  }

  const abrirModal = () => {
    setComponenteModal(
      <div>
        {metaDatosOrdenados.map((metadato) => (
          <MetadatosCampos {...metadatoCamposProps} key={metadato.idMetaDato} metadato={metadato} />
        ))}
      </div>
    )
    dispatch(
      setModal({
        size: 'lg',
        title: 'Características del Producto',
        type: 'jsx',
        body: '',
        show: true,
        editMode: false,
        modalId: 'metadatos-caracteristicas'
      })
    )
  }

  return (
    <>
      {esPublicar && (
        <p className="descripcion-cuadro-publicacion">
          Cuanta más información tenga la publicación, más fácil será vender el producto.
        </p>
      )}
      {(mostrarConModal
        ? metaDatosOrdenados.slice(0, MAX_METADATOS_SIN_MODAL)
        : metaDatosOrdenados
      ).map((metadato) => (
        <MetadatosCampos {...metadatoCamposProps} key={metadato.idMetaDato} metadato={metadato} />
      ))}
      {mostrarConModal && (
        <div className="row mt-2">
          <div className="col-12 d-flex justify-content-end">
            <button type="button" className={styles.btnVerMas} onClick={abrirModal}>
              Ver todas las características ({metaDatosOrdenados.length})
            </button>
          </div>
        </div>
      )}
      <GenericModal component={componenteModal} modalId="metadatos-caracteristicas" />
    </>
  )
}

Metadatos.propTypes = {
  idPublicacion: PropTypes.number,
  editable: PropTypes.bool,
  idCategoria: PropTypes.number,
  esPublicar: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.shape({
    metadatos: PropTypes.array
  }),
  onChangeExterno: PropTypes.func
}

export default Metadatos
